import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Buildings, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAcademy } from "@/contexts/AcademyContext";

export default function AcademyJoin() {
  const { code } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { reload } = useAcademy();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/academies/invite/${code}`)
      .then(({ data }) => setPreview(data))
      .catch((e) => setError(e?.response?.data?.detail || "Invite not found"))
      .finally(() => setLoading(false));
  }, [code]);

  const join = async () => {
    if (!user) { nav(`/login?next=/academy-join/${code}`); return; }
    setJoining(true);
    try {
      await api.post(`/academies/join/${code}`);
      toast.success("Joined academy");
      await reload();
      nav("/academy");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to join");
    } finally {
      setJoining(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" data-testid="academy-join-page">
      <div className="max-w-lg w-full border border-white/10 bg-white/[0.02] p-8 space-y-6">
        <div className="flex items-center gap-3">
          <Buildings size={36} className="text-brand-accent" weight="duotone" />
          <div className="label-tiny text-brand-accent tracking-widest">// ACADEMY INVITE</div>
        </div>
        {loading && <div className="label-tiny text-text-secondary" data-testid="academy-join-loading">Verifying invite...</div>}
        {error && (
          <div data-testid="academy-join-error">
            <div className="font-heading text-2xl mb-2">Invite not available</div>
            <div className="text-text-secondary text-sm">{error}</div>
            <Link to="/" className="mt-4 inline-flex items-center gap-1 text-sm text-brand-accent">Back home <ArrowRight size={14} /></Link>
          </div>
        )}
        {preview && (
          <>
            <div>
              <div className="font-heading text-3xl" data-testid="academy-join-name">{preview.academy.name}</div>
              {preview.academy.description && (
                <div className="text-text-secondary text-sm mt-1">{preview.academy.description}</div>
              )}
              <div className="mt-3 label-tiny text-[11px] text-text-secondary">
                You&apos;ve been invited to join as <span className="text-brand-accent">{(preview.role || "").replace(/_/g, " ").toUpperCase()}</span>
              </div>
            </div>
            <button
              onClick={join}
              disabled={joining}
              className="w-full bg-brand-accent text-surface-base py-3 text-sm label-tiny tracking-wider disabled:opacity-60"
              data-testid="academy-join-submit"
            >
              {joining ? "JOINING..." : user ? "ACCEPT & JOIN" : "SIGN IN TO JOIN"}
            </button>
            {!user && (
              <div className="text-[11px] text-text-secondary text-center">
                You&apos;ll be redirected to sign in first.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
